import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ShoppingCart, User, LogIn, LogOut, LayoutDashboard } from '../../lib/lucide-proxy';
import { useAuth } from '../../contexts/AuthContext';
import { useCart } from '../../contexts/CartContext';
import Button from '../ui/Button';
import Cart from '../cart/Cart';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const { user, profile, signOut, loading } = useAuth();
  const { getTotalItems } = useCart();
  const navigate = useNavigate();

  const navigation = [
    { name: 'Cervejarias', href: '/cervejarias' },
    { name: 'Enciclopédia', href: '/cervejas' },
    { name: 'Blog', href: '/blog' },
    { name: 'Experiência', href: '/como-funciona' }
  ];

  const handleLogout = async () => {
    setIsMenuOpen(false);
    await signOut();
  };

  const getDashboardLink = () => {
    if (!profile) return '/dashboard';
    if (profile.role === 'admin' || profile.role === 'brewery_owner') return '/admin';
    return '/dashboard';
  };

  const totalItems = getTotalItems();

  return (
    <>
      <header className="bg-white shadow-soft sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo - BRANDING UPDATE */}
            <Link to="/" className="flex items-center space-x-2 group">
              <div className="w-8 h-8 bg-primary-500 rounded-lg flex items-center justify-center group-hover:bg-primary-600 transition-colors">
                <span className="text-white font-bold text-sm">C</span>
              </div>
              <span className="font-display font-bold text-xl text-dark-900">
                Cerveja <span className="text-primary-500 group-hover:text-primary-600 transition-colors">Delivery</span>
              </span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex space-x-8">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className="text-dark-700 hover:text-primary-500 font-medium transition-colors duration-200"
                >
                  {item.name}
                </Link>
              ))}
            </nav>

            {/* Right Section */}
            <div className="flex items-center space-x-4">
              {/* Cart Button */}
              <button
                onClick={() => setIsCartOpen(true)}
                className="relative p-2 text-dark-700 hover:text-primary-500 transition-colors duration-200 group"
              >
                <ShoppingCart className="w-6 h-6 group-hover:scale-110 transition-transform" />
                {totalItems > 0 && (
                  <span className="absolute -top-1 -right-1 bg-primary-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center animate-bounce-slow">
                    {totalItems}
                  </span>
                )}
              </button>

              {/* Auth Section */}
              {user ? (
                <div className="flex items-center space-x-3">
                  <Link
                    to={getDashboardLink()}
                    className="flex items-center space-x-2 text-dark-700 hover:text-primary-500 transition-colors duration-200 bg-gray-50 hover:bg-primary-50 px-3 py-2 rounded-lg"
                  >
                    {profile?.role === 'admin' || profile?.role === 'brewery_owner' ? (
                      <LayoutDashboard className="w-5 h-5" />
                    ) : (
                      <User className="w-5 h-5" />
                    )}
                    <span className="hidden sm:block font-medium truncate max-w-[100px]">
                      {loading ? '...' : (profile?.full_name?.split(' ')[0] || 'Conta')}
                    </span>
                  </Link>
                  
                  <button 
                    onClick={handleLogout}
                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    title="Sair"
                  >
                    <LogOut className="w-5 h-5" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center space-x-3">
                  <Button variant="ghost" size="sm" onClick={() => navigate('/login')} className="flex items-center space-x-2">
                    <LogIn className="w-4 h-4" />
                    <span className="hidden sm:block">Entrar</span>
                  </Button>
                  <Button variant="primary" size="sm" onClick={() => navigate('/register')} className="hidden sm:flex">
                    Criar Conta
                  </Button>
                </div>
              )}

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="md:hidden p-2 text-dark-700 hover:text-primary-500 transition-colors duration-200"
              >
                {isMenuOpen ? (
                  <X className="w-6 h-6" />
                ) : (
                  <Menu className="w-6 h-6" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden border-t border-gray-200 bg-white overflow-hidden shadow-lg"
            >
              <div className="px-4 py-4 space-y-4">
                {navigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className="block text-dark-700 hover:text-primary-500 font-medium transition-colors duration-200 py-2"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                ))}
                <div className="pt-4 border-t border-gray-200 space-y-3">
                  {user ? (
                    <>
                      <Link
                        to={getDashboardLink()}
                        className="flex items-center space-x-2 text-dark-900 font-semibold py-2"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        <LayoutDashboard className="w-5 h-5 text-primary-500" />
                        <span>Painel / Perfil</span>
                      </Link>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleLogout}
                        className="w-full border-red-200 text-red-500 hover:bg-red-50 hover:text-red-600 hover:border-red-300"
                      >
                        Sair da Conta
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => { navigate('/login'); setIsMenuOpen(false); }}
                        className="w-full justify-start"
                      >
                        Entrar
                      </Button>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => { navigate('/register'); setIsMenuOpen(false); }}
                        className="w-full"
                      >
                        Criar Conta
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>
      <Cart isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />
    </>
  );
};

export default Header;